// src/components/Admin/AccountSettings.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import UserAvatar from '../../components/UI/UserAvatar';
import BackgroundPatternPicker from '../../components/UI/BackgroundPatternPicker';
import { ChromePicker } from 'react-color';
import {
  BACKGROUND_PATTERN_PLACEHOLDER,
  DEFAULT_BACKGROUND_PATTERN,
  DEFAULT_BACKGROUND_PATTERN_OPACITY,
  getBackgroundPatternStyle,
} from '../../components/utils/backgroundPatterns';
import { buildPublicProfilePath } from '../../components/utils/profilePaths';

const AccountSettings = () => {
  const { currentUser, logout, updateCurrentUser } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const settingsPreviewRef = useRef(null);
  const patternPickerRef = useRef(null);

  // Refs for the color pickers
  const primaryPickerRef = useRef(null);
  const accentPickerRef = useRef(null);
  const primaryTextPickerRef = useRef(null);
  const accentTextPickerRef = useRef(null);
  const backgroundPickerRef = useRef(null);
  const backgroundTextPickerRef = useRef(null);

  // Default theme colors
  const defaultThemePrimary = '#1A140F';
  const defaultThemePrimaryText = '#FAF6F2';
  const defaultThemeAccent = '#FAF6F2';
  const defaultThemeAccentText = '#1A140F';
  const defaultThemeBackground = '#FFFFFF';
  const defaultThemeBackgroundText = '#1A140F';
  const defaultLayoutStyle = '2';

  // Initialize profile state
  const [profile, setProfile] = useState(
    currentUser || {
      _id: '',
      username: '',
      name: '',
      email: '',
      bio: '',
      avatarUrl: '/images/user-image.png',
      themePrimary: defaultThemePrimary,
      themeAccent: defaultThemeAccent,
      themePrimaryText: defaultThemePrimaryText,
      themeAccentText: defaultThemeAccentText,
      themeBackground: defaultThemeBackground,
      themeBackgroundText: defaultThemeBackgroundText,
      backgroundPattern: DEFAULT_BACKGROUND_PATTERN,
      backgroundPatternOpacity: DEFAULT_BACKGROUND_PATTERN_OPACITY,
      layoutStyle: defaultLayoutStyle,
    }
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Color picker visibility
  const [showPrimaryColorPicker, setShowPrimaryColorPicker] = useState(false);
  const [showAccentColorPicker, setShowAccentColorPicker] = useState(false);
  const [showPrimaryTextColorPicker, setShowPrimaryTextColorPicker] = useState(false);
  const [showAccentTextColorPicker, setShowAccentTextColorPicker] = useState(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false);
  const [showBackgroundTextColorPicker, setShowBackgroundTextColorPicker] = useState(false);
  const [showPatternPicker, setShowPatternPicker] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setProfile({
        ...currentUser,
        themePrimary: currentUser.themePrimary || defaultThemePrimary,
        themeAccent: currentUser.themeAccent || defaultThemeAccent,
        themePrimaryText: currentUser.themePrimaryText || defaultThemePrimaryText,
        themeAccentText: currentUser.themeAccentText || defaultThemeAccentText,
        themeBackground: currentUser.themeBackground || defaultThemeBackground,
        themeBackgroundText: currentUser.themeBackgroundText || defaultThemeBackgroundText,
        backgroundPattern: currentUser.backgroundPattern || DEFAULT_BACKGROUND_PATTERN,
        backgroundPatternOpacity:
          Number.isFinite(Number(currentUser.backgroundPatternOpacity))
            ? Number(currentUser.backgroundPatternOpacity)
            : DEFAULT_BACKGROUND_PATTERN_OPACITY,
        layoutStyle: currentUser.layoutStyle || defaultLayoutStyle,
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Update CSS variables for live preview
  useEffect(() => {
    const el = settingsPreviewRef.current;
    if (!el) return;

    if (profile.themePrimary) {
      el.style.setProperty('--theme-primary', profile.themePrimary);
    }
    if (profile.themeAccent) {
      el.style.setProperty('--theme-accent', profile.themeAccent);
    }
    if (profile.themePrimaryText) {
      el.style.setProperty('--theme-primary-text', profile.themePrimaryText);
    }
    if (profile.themeAccentText) {
      el.style.setProperty('--theme-accent-text', profile.themeAccentText);
    }
    if (profile.themeBackground) {
      el.style.setProperty('--theme-background', profile.themeBackground);
    }
    if (profile.themeBackgroundText) {
      el.style.setProperty('--theme-background-text', profile.themeBackgroundText);
    }
  }, [
    profile.themePrimary,
    profile.themeAccent,
    profile.themePrimaryText,
    profile.themeAccentText,
    profile.themeBackground,
    profile.themeBackgroundText,
  ]);

  const buildProfileState = (data) => ({
    ...profile,
    ...data,
    _id: data._id || profile._id || currentUser?._id || '',
    username: data.username || profile.username || currentUser?.username || '',
    name: data.name || profile.name || '',
    email: data.email || profile.email || '',
    bio: data.bio || profile.bio || '',
    avatarUrl: data.avatarUrl || '/images/user-image.png',
    themePrimary: data.themePrimary || defaultThemePrimary,
    themeAccent: data.themeAccent || defaultThemeAccent,
    themePrimaryText: data.themePrimaryText || defaultThemePrimaryText,
    themeAccentText: data.themeAccentText || defaultThemeAccentText,
    themeBackground: data.themeBackground || defaultThemeBackground,
    themeBackgroundText: data.themeBackgroundText || defaultThemeBackgroundText,
    backgroundPattern: data.backgroundPattern || DEFAULT_BACKGROUND_PATTERN,
    backgroundPatternOpacity:
      Number.isFinite(Number(data.backgroundPatternOpacity))
        ? Number(data.backgroundPatternOpacity)
        : DEFAULT_BACKGROUND_PATTERN_OPACITY,
    layoutStyle: data.layoutStyle || defaultLayoutStyle,
    token: currentUser.token,
  });

  const profileLink = profile._id
    ? `${window.location.origin}${buildPublicProfilePath(profile)}`
    : '';
  const hasCustomAvatar = profile.avatarUrl && profile.avatarUrl !== '/images/user-image.png';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleLayoutChange = (value) => {
    setProfile((prev) => ({
      ...prev,
      layoutStyle: value,
    }));
  };

  const handleSave = () => {
    setLoading(true);
    setError(null);
    setSuccessMsg('');

    const { token, ...profileData } = profile;

    fetch('/api/users/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify(profileData),
    })
      .then((res) => {
        if (!res.ok) {
          alert('Failed to update profile. Please try again later.');
          return Promise.reject(new Error('Failed to update profile'));
        }
        return res.json();
      })
      .then((data) => {
        const updatedProfile = buildProfileState(data);

        setProfile(updatedProfile);
        updateCurrentUser(updatedProfile);
        setSuccessMsg('Profile updated successfully!');
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error saving profile:', err);
        setError(err.message);
        setLoading(false);
      });
  };

  const handleChangePassword = () => {
    setPasswordError('');

    if (!newPassword) {
      setPasswordError('Please enter a new password.');
      return;
    }

    setLoading(true);

    fetch('/api/users/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ newPassword }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to update password');
        }
        return res.json();
      })
      .then(() => {
        alert('Your password has been successfully updated.');
        setNewPassword('');
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error updating password:', err);
        setPasswordError(err.message);
        setLoading(false);
      });
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 2 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('Sorry, your avatar must be smaller than 2 MB. Please choose a smaller image.');
      e.target.value = null;
      return;
    }

    setError(null);

    const formData = new FormData();
    formData.append('avatar', file);

    fetch('/api/users/avatar', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: formData,
    })
      .then((res) => {
        if (res.status === 413) {
          alert('Image too large. Please pick one under 2 MB.');
          e.target.value = null;
          return Promise.reject(new Error('Image too large'));
        }
        if (!res.ok) {
          alert('Failed to upload avatar. Try again or use a smaller image.');
          e.target.value = null;
          return Promise.reject(new Error('Failed to upload avatar'));
        }
        return res.json();
      })
      .then((data) => {
        const updatedProfile = buildProfileState(data);

        setProfile(updatedProfile);
        updateCurrentUser(updatedProfile);
        e.target.value = null;
      })
      .catch(() => {
        console.error('Upload aborted or failed.');
        e.target.value = null;
      });
  };

  const handleRemoveAvatar = () => {
    setError(null);

    fetch('/api/users/avatar', {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${currentUser.token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          alert('Failed to remove avatar. Please try again.');
          return Promise.reject(new Error('Failed to remove avatar'));
        }
        return res.json();
      })
      .then((data) => {
        const updatedProfile = buildProfileState(data);
        setProfile(updatedProfile);
        updateCurrentUser(updatedProfile);
      })
      .catch((err) => {
        console.error('Error removing avatar:', err);
      });
  };

  const handleCopyLink = () => {
    if (profileLink) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(profileLink)
          .then(() => alert('Profile link copied to clipboard!'))
          .catch((err) => {
            console.error('Error copying profile link:', err);
            alert('Failed to copy link');
          });
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = profileLink;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          alert('Profile link copied to clipboard!');
        } catch (err) {
          console.error('Fallback: Unable to copy', err);
          alert('Failed to copy link');
        }
        document.body.removeChild(textArea);
      }
    }
  };

  const handleDeleteAccount = () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);

    fetch(`/api/users/profile/${profile._id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentUser.token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to delete account');
        }
        return res.json();
      })
      .then(() => {
        alert('Account deleted successfully');

        if (logout) {
          logout();
        }

        window.location.href = '/login';
      })
      .catch((err) => {
        console.error('Error deleting account:', err);
        setError(err.message);
        setLoading(false);
      });
  };

  const handleResetColors = () => {
    setProfile((prev) => ({
      ...prev,
      themePrimary: defaultThemePrimary,
      themeAccent: defaultThemeAccent,
      themePrimaryText: defaultThemePrimaryText,
      themeAccentText: defaultThemeAccentText,
      themeBackground: defaultThemeBackground,
      themeBackgroundText: defaultThemeBackgroundText,
    }));
  };

  const handlePatternChange = (backgroundPattern) => {
    setProfile((prev) => ({
      ...prev,
      backgroundPattern,
    }));
  };

  const handlePatternOpacityChange = (backgroundPatternOpacity) => {
    setProfile((prev) => ({
      ...prev,
      backgroundPatternOpacity,
    }));
  };

  const hasCustomPattern =
    profile.backgroundPattern !== DEFAULT_BACKGROUND_PATTERN ||
    Number(profile.backgroundPatternOpacity) !== DEFAULT_BACKGROUND_PATTERN_OPACITY;

  const handleResetPattern = () => {
    setProfile((prev) => ({
      ...prev,
      backgroundPattern: DEFAULT_BACKGROUND_PATTERN,
      backgroundPatternOpacity: DEFAULT_BACKGROUND_PATTERN_OPACITY,
    }));
    setShowPatternPicker(false);
  };

  const patternPreviewStyle = hasCustomPattern
    ? getBackgroundPatternStyle({
        pattern: profile.backgroundPattern,
        opacity: profile.backgroundPatternOpacity,
        backgroundColor: profile.themeBackground || defaultThemeBackground,
        foregroundColor: profile.themeBackgroundText || defaultThemeBackgroundText,
      })
    : {
        backgroundColor: BACKGROUND_PATTERN_PLACEHOLDER,
        backgroundImage: 'none',
      };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPrimaryColorPicker && primaryPickerRef.current && !primaryPickerRef.current.contains(event.target)) {
        setShowPrimaryColorPicker(false);
      }
      if (showAccentColorPicker && accentPickerRef.current && !accentPickerRef.current.contains(event.target)) {
        setShowAccentColorPicker(false);
      }
      if (
        showPrimaryTextColorPicker &&
        primaryTextPickerRef.current &&
        !primaryTextPickerRef.current.contains(event.target)
      ) {
        setShowPrimaryTextColorPicker(false);
      }
      if (
        showAccentTextColorPicker &&
        accentTextPickerRef.current &&
        !accentTextPickerRef.current.contains(event.target)
      ) {
        setShowAccentTextColorPicker(false);
      }
      if (
        showBackgroundColorPicker &&
        backgroundPickerRef.current &&
        !backgroundPickerRef.current.contains(event.target)
      ) {
        setShowBackgroundColorPicker(false);
      }
      if (
        showBackgroundTextColorPicker &&
        backgroundTextPickerRef.current &&
        !backgroundTextPickerRef.current.contains(event.target)
      ) {
        setShowBackgroundTextColorPicker(false);
      }
      if (showPatternPicker && patternPickerRef.current && !patternPickerRef.current.contains(event.target)) {
        setShowPatternPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    showPrimaryColorPicker,
    showAccentColorPicker,
    showPrimaryTextColorPicker,
    showAccentTextColorPicker,
    showBackgroundColorPicker,
    showBackgroundTextColorPicker,
    showPatternPicker,
  ]);

  return (
    <div className="row" ref={settingsPreviewRef}>
      <div className="col-xs-12">
        {loading && <div className="success-disappear-badge">Loading profile...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && !error && (
          <div className="card form">
            {successMsg && <div className="success-disappear-badge">{successMsg}</div>}

            <div className="row spacing_s margin-top-s">
              <div className="col-xs-12 col-sm-6">
                <div className="text-uppercase">Name</div>
                <input type="text" name="name" value={profile.name} onChange={handleChange} />
              </div>

              <div className="col-xs-12 col-sm-6">
                <div className="text-uppercase">Email Address</div>
                <input type="email" name="email" value={profile.email} onChange={handleChange} />
              </div>
            </div>

            <div className="row spacing_s">
              <div className="col-xs-12">
                <div className="text-uppercase">Bio</div>
                <textarea name="bio" value={profile.bio} onChange={handleChange} />
              </div>
            </div>

            <div className="row spacing_s middle-xs">
              <div className="col-xs-12 col-sm-6">
                <div className="text-uppercase">Password</div>
                <div className="form-field-with-button">
                  <input
                    type="password"
                    name="newPassword"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button className="small line" onClick={handleChangePassword}>
                    Change password
                  </button>
                </div>
                {passwordError && <div className="error">{passwordError}</div>}
              </div>

              <div className="col-xs-12 col-sm-6">
                <div className="text-uppercase">Profile Link</div>
                <div className="form-field-with-button">
                  <input type="text" value={profileLink} readOnly />
                  <button className="small line" onClick={handleCopyLink}>
                    Copy Link
                  </button>
                </div>
              </div>
            </div>

            <div className="row spacing_s padding-top-m padding-bottom-m">
              <div className="col-xs-12 col-sm-6 col-lg-3">
                <div className="text-uppercase margin-bottom-m">Photo</div>

                <div className="user-profile-image-upload-wrapper">
                  <div className="user-profile-image-upload">
                    <UserAvatar avatarUrl={profile.avatarUrl} userName={profile.name} />
                  </div>

                  <div>
                    <button
                      type="button"
                      className="small line"
                      onClick={hasCustomAvatar ? handleRemoveAvatar : handleAvatarClick}
                    >
                      {hasCustomAvatar ? 'Remove' : 'Upload'}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      accept="image/*"
                    />
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-6 col-lg-3">
                <div className="text-uppercase margin-bottom-m">Pattern</div>

                <div className="background-pattern-selector-wrapper">
                  <div className="background-pattern-selector-group" ref={patternPickerRef}>
                    <button
                      type="button"
                      className="background-pattern-selector background-pattern-selector-trigger"
                      onClick={() => setShowPatternPicker((prev) => !prev)}
                      aria-expanded={showPatternPicker}
                      aria-haspopup="dialog"
                    >
                      <div className="background-pattern-preview" style={patternPreviewStyle} />
                    </button>

                    <div>
                      <button
                        type="button"
                        className="small line"
                        onClick={hasCustomPattern ? handleResetPattern : () => setShowPatternPicker((prev) => !prev)}
                        aria-expanded={!hasCustomPattern ? showPatternPicker : undefined}
                        aria-haspopup={!hasCustomPattern ? 'dialog' : undefined}
                      >
                        {hasCustomPattern ? 'Reset' : 'Select'}
                      </button>
                    </div>

                    {showPatternPicker && (
                      <BackgroundPatternPicker
                        pattern={profile.backgroundPattern}
                        opacity={profile.backgroundPatternOpacity}
                        previewBackground={profile.themeBackground || defaultThemeBackground}
                        previewForeground={profile.themeBackgroundText || defaultThemeBackgroundText}
                        onPatternChange={handlePatternChange}
                        onOpacityChange={handlePatternOpacityChange}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-6 col-lg-3">
                <div className="text-uppercase margin-bottom-m">Color</div>
                <div className="setting-branding-container">
                  <div className="setting-branding-color-column-group-wrapper">
                    <div className="setting-branding-color-column-group">
                      <div className="setting-branding-color-column">
                        <div
                          className="color-preview-wrap has-tooltip has-tooltip-circle"
                          onClick={() => setShowPrimaryColorPicker(true)}
                        >
                          <span
                            className="color-preview"
                            style={{ backgroundColor: profile.themePrimary }}
                          />
                          <span className="instant-tooltip">Primary</span>
                        </div>

                        {showPrimaryColorPicker && (
                          <div className="color-picker" ref={primaryPickerRef}>
                            <ChromePicker
                              color={profile.themePrimary}
                              onChangeComplete={(color) =>
                                setProfile((prev) => ({ ...prev, themePrimary: color.hex }))
                              }
                            />
                          </div>
                        )}
                      </div>

                      <div className="setting-branding-color-column">
                        <div
                          className="color-preview-wrap small has-tooltip"
                          onClick={() => setShowPrimaryTextColorPicker(true)}
                        >
                          <span
                            className="color-preview small"
                            style={{ backgroundColor: profile.themePrimaryText }}
                          />
                          <span className="instant-tooltip">Primary text</span>
                        </div>

                        {showPrimaryTextColorPicker && (
                          <div className="color-picker" ref={primaryTextPickerRef}>
                            <ChromePicker
                              color={profile.themePrimaryText}
                              onChangeComplete={(color) =>
                                setProfile((prev) => ({ ...prev, themePrimaryText: color.hex }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="setting-branding-color-column-group">
                      <div className="setting-branding-color-column">
                        <div
                          className="color-preview-wrap has-tooltip has-tooltip-circle"
                          onClick={() => setShowAccentColorPicker(true)}
                        >
                          <span
                            className="color-preview"
                            style={{ backgroundColor: profile.themeAccent }}
                          />
                          <span className="instant-tooltip">Accent</span>
                        </div>

                        {showAccentColorPicker && (
                          <div className="color-picker" ref={accentPickerRef}>
                            <ChromePicker
                              color={profile.themeAccent}
                              onChangeComplete={(color) =>
                                setProfile((prev) => ({ ...prev, themeAccent: color.hex }))
                              }
                            />
                          </div>
                        )}
                      </div>

                      <div className="setting-branding-color-column">
                        <div
                          className="color-preview-wrap small has-tooltip"
                          onClick={() => setShowAccentTextColorPicker(true)}
                        >
                          <span
                            className="color-preview small"
                            style={{ backgroundColor: profile.themeAccentText }}
                          />
                          <span className="instant-tooltip">Accent text</span>
                        </div>

                        {showAccentTextColorPicker && (
                          <div className="color-picker" ref={accentTextPickerRef}>
                            <ChromePicker
                              color={profile.themeAccentText}
                              onChangeComplete={(color) =>
                                setProfile((prev) => ({ ...prev, themeAccentText: color.hex }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="setting-branding-color-column-group">
                      <div className="setting-branding-color-column">
                        <div
                          className="color-preview-wrap has-tooltip has-tooltip-circle"
                          onClick={() => setShowBackgroundColorPicker(true)}
                        >
                          <span
                            className="color-preview"
                            style={{ backgroundColor: profile.themeBackground }}
                          />
                          <span className="instant-tooltip">Background</span>
                        </div>

                        {showBackgroundColorPicker && (
                          <div className="color-picker" ref={backgroundPickerRef}>
                            <ChromePicker
                              color={profile.themeBackground}
                              onChangeComplete={(color) =>
                                setProfile((prev) => ({ ...prev, themeBackground: color.hex }))
                              }
                            />
                          </div>
                        )}
                      </div>

                      <div className="setting-branding-color-column">
                        <div
                          className="color-preview-wrap small has-tooltip"
                          onClick={() => setShowBackgroundTextColorPicker(true)}
                        >
                          <span
                            className="color-preview small"
                            style={{ backgroundColor: profile.themeBackgroundText }}
                          />
                          <span className="instant-tooltip">Background text</span>
                        </div>

                        {showBackgroundTextColorPicker && (
                          <div className="color-picker" ref={backgroundTextPickerRef}>
                            <ChromePicker
                              color={profile.themeBackgroundText}
                              onChangeComplete={(color) =>
                                setProfile((prev) => ({ ...prev, themeBackgroundText: color.hex }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button className="small line" onClick={handleResetColors}>
                    Reset
                  </button>
                </div>
              </div>

              <div className="col-xs-12 col-sm-6 col-lg-3">
                <div className="text-uppercase margin-bottom-m">Layout</div>

                <div className="layout-style-options-wrap">
                  <label className="layout-style-option">
                    <input
                      type="radio"
                      name="layoutStyle"
                      value="1"
                      checked={profile.layoutStyle === '1'}
                      onChange={() => handleLayoutChange('1')}
                      className="layout-style-radio"
                    />
                    <span className="layout-style-image-wrap">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/i_Grid.svg`}
                        alt="Grid layout"
                        className="layout-style-image layout-style-image-grid"
                      />
                    </span>
                  </label>

                  <label className="layout-style-option">
                    <input
                      type="radio"
                      name="layoutStyle"
                      value="2"
                      checked={profile.layoutStyle === '2'}
                      onChange={() => handleLayoutChange('2')}
                      className="layout-style-radio"
                    />
                    <span className="layout-style-image-wrap">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/i_List.svg`}
                        alt="List layout"
                        className="layout-style-image layout-style-image-list"
                      />
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="row spacing_s">
              <div className="col-xs-12">
                <div className="setting-buttons">
                  <button className="small line" onClick={handleSave}>
                    Save Account Settings
                  </button>
                  <button className="small line red" onClick={handleDeleteAccount}>
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;
